<template>
  <div class="container mg-collection-report-card pb-4">
    <div
      v-if="!loaded"
      class="d-flex justify-content-center align-items-center spinner-container"
    >
      <spinner />
    </div>
    <div v-else-if="collection?.in_series?.withdrawn && !collection?.withdrawn">
      <div class="alert alert-warning" role="alert">
        {{ uiText["collection_biobank_withdrawn"] }}
      </div>
    </div>

    <div
      v-else-if="collection?.withdrawn"
      class="alert alert-warning"
      role="alert"
    >
      {{ uiText["collection_withdrawn"] }}
    </div>

    <div
      v-if="loaded && collection && bioschemasJsonld"
      v-html="bioschemasJsonld"
    />

    <div class="container-fluid" v-if="loaded && collection">
      <div class="row">
        <div class="col my-3 shadow-sm d-flex p-2 align-items-center bg-white">
          <Breadcrumb
            class="directory-nav"
            :crumbs="{
              [uiText['home']]: '../',
              [collection?.in_series?.title[0]]: `../datasetseries/${collection?.in_series?.id}`,
              [collection?.title[0]]: `../dataset/${collection?.id}`,
            }"
            useRouterLink
          />
          <check-out
            v-if="collection"
            class="ml-auto"
            :bookmark="false"
            :disabled="collection?.in_series?.withdrawn"
          />
        </div>
      </div>

      <div class="row">
        <div class="col p-0">
          <ReportTitle type="Dataset" :name="collection?.title" />

          <div class="container p-0">
            <div class="row">
              <div class="col-md-8">
                <ReportCollectionDetails
                  v-if="collection"
                  :in_series="info.in_series"
                  :collection="collection"
                />
                <Tabs
                  v-if="samples || analytics"
                  :tabs="tabs"
                  @update:active-tab="changeTab"
                  class="mt-1"
                />
                <div v-if="activeTab === 'Sample'">
                  <div
                  v-for="(sample, index) in samples"
                  :key="sample.id"
                  >
                  <hr v-if="index" />
                  <div class="d-flex align-items-center">
                    <CollectionTitle
                    :title="sample.title[0]"
                    :id="sample.id"
                    />
                  </div>
                  <CollapseComponent>
                      <ViewGenerator
                        v-if="sample"
                        class="collection-view"
                        :viewmodel="getDistributionDetails(sample).viewmodel"
                      />
                    </CollapseComponent>
                  </div>
                </div>
                <div v-if="activeTab === 'Analytics'">
                  <div
                  v-for="(analytic, index) in analytics"
                  :key="analytic.id"
                  >
                  <hr v-if="index" />
                  <div class="d-flex align-items-center">
                    <CollectionTitle
                    :title="analytic.title[0]"
                    :id="analytic.id"
                    />
                  </div>
                  <CollapseComponent>
                      <ViewGenerator
                        v-if="analytic"
                        class="collection-view"
                        :viewmodel="getDistributionDetails(analytic).viewmodel"
                      />
                    </CollapseComponent>
                  </div>
                </div>
              </div>
              <!-- Right side card -->
              <CollectionReportInfoCard :info="info" :collection="collection" />
            </div>
            <!-- facts data -->
            <!--
            <div class="row" v-if="facts && facts.length > 0">
              <FactsTable class="w-100 px-3" :attribute="facts" />
            </div>
            -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { Breadcrumb, Spinner } from "molgenis-components";
import Tabs from "../components/Tabs.vue";
import CollectionTitle from "../components/report-components/CollectionTitle.vue";
import CollapseComponent from "../components/report-components/CollapseComponent.vue";
import ViewGenerator from "../components/generators/ViewGenerator.vue";
import CheckOut from "../components/checkout-components/CheckOut.vue";
import CollectionReportInfoCard from "../components/report-components/CollectionReportInfoCard.vue";
import FactsTable from "../components/report-components/FactsTable.vue";
import ReportCollectionDetails from "../components/report-components/ReportCollectionDetails.vue";
import ReportTitle from "../components/report-components/ReportTitle.vue";
import useErrorHandler from "../composables/errorHandler";
import { mapCollectionToBioschemas } from "../functions/bioschemasMapper";
import { collectionReportInformation, getDistributionDetails } from "../functions/viewmodelMapper";
import { useCollectionStore } from "../stores/collectionStore";
// import { useQualitiesStore } from "../stores/qualitiesStore";
import { useSettingsStore } from "../stores/settingsStore";

const settingsStore = useSettingsStore();
const collectionStore = useCollectionStore();
// const qualitiesStore = useQualitiesStore();

const collection = ref();
const samples = ref();
const analytics = ref();

// const facts = ref({});
const route = useRoute();
const { setError, clearError } = useErrorHandler();

let loaded = ref(false);

loadCollectionReport(route.params.id);

watch(route, async (route) => {
  loadCollectionReport(route.params.id);
});

const uiText = computed(() => {
  return settingsStore.uiText;
});

const info = computed(() => {
  return collection.value ? collectionReportInformation(collection.value) : {};
});

const bioschemasJsonld = computed(() => {
  if (collection.value?.in_series) {
    return wrapBioschema(mapCollectionToBioschemas(collection.value));
  } else {
    return undefined;
  }
});

type DistributionDetailTab = "Sample" | "Analytics";
const activeTab = ref<DistributionDetailTab>("Sample");

const tabs = computed(() => {
  return {
    Sample: {
      id: "Sample",
      label: "Sample distributions",
      active: activeTab.value === "Sample",
      disabled: !samples,
    },
    Analytics: {
      id: "Analytics",
      label: "Analytics distributions",
      active: activeTab.value === "Analytics",
      disabled: !analytics,
    },
  };
});

function changeTab(tab: DistributionDetailTab) {
  activeTab.value = tab;
}

function loadCollectionReport(id: string) {
  loaded.value = false;
  clearError();
  const collectionsPromise = collectionStore
    .getCollectionReport(id)
    .then((result) => {
      if (result.Collections?.length) {
        collection.value = result.Collections[0];
        loadSampleReport();
        loadAnalyticsReport();
      } else {
        setError("Collection not found");
      }
      // facts.value =
      //   result.CollectionFacts && result.CollectionFacts.length
      //     ? result.CollectionFacts
      //     : {};
    });
  // const qualitiesPromise = qualitiesStore.getQualityStandardInformation();
  Promise.all([collectionsPromise]).then(() => {
    loaded.value = true;
  });
  // Promise.all([qualitiesPromise, collectionsPromise]).then(() => {
  //   loaded.value = true;
  // });
}

async function loadSampleReport() {
  if (!collection.value.sample) return;

  const reports = await Promise.all(
    collection.value.sample.map(async (sample) => {
      const result = await collectionStore.getDistributionReport(sample.id);

      if (result.Distribution?.length) {
        return result.Distribution[0];
      }

      setError(`Samples distribution with ${sample.id} not found`);
      return null;
    })
  );

  samples.value = reports.filter(Boolean);
}

async function loadAnalyticsReport() {
  if (!collection.value.analytics) return;

  const reports = await Promise.all(
    collection.value.analytics.map(async (analytic) => {
      const result = await collectionStore.getDistributionReport(analytic.id);

      if (result.Distribution?.length) {
        return result.Distribution[0];
      }

      setError(`Analytics distribution with ${analytic.id} not found`);
      return null;
    })
  );

  analytics.value = reports.filter(Boolean);
}

function wrapBioschema(schemaData) {
  /** ignore because it is not useless ;) */
  return `<script type="application/ld+json">${JSON.stringify(
    schemaData
  )}<\/script>`;
}
</script>

<style scoped>
.spinner-container {
  height: 70vh;
}
</style>
